import React, { useState, useEffect, useRef } from 'react';
import { Select, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { fetchAllUsers } from 'src/redux/features/users/usersSlice';

interface ResourceSearchableDropdownProps {
  onChange: (value: { label: string; value: string } | null) => void;
  selected?: { label: string; value: string } | null;
  placeholder?: string;
  internalResourceFlag?: boolean;
  condition?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const ResourceSearchableDropdown: React.FC<ResourceSearchableDropdownProps> = ({
  onChange,
  selected = null,
  placeholder,
  internalResourceFlag,
  condition = false,
  style,
  disabled,
}) => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const activeWeekRequest = useRef<any>(null);

  const handleSelect = (val) => {
    onChange(val);
    setSearchText('');
    setOptions([]);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText.length >= 3) {
        fetchUsers(searchText);
      } else {
        if (activeWeekRequest.current) {
          activeWeekRequest.current.abort();
        }
        setOptions([]);
      }
    }, 100);

    return () => clearTimeout(handler);
  }, [searchText]);

  const fetchUsers = async (query) => {
    // cancel previous request if any
    if (activeWeekRequest.current) {
      activeWeekRequest.current.abort();
    }

    setLoading(true);

    const promise = dispatch(
      fetchAllUsers({
        query,
        internalResources: internalResourceFlag,
      }),
    );

    // store latest request
    activeWeekRequest.current = promise;

    promise
      .then((response) => {
        if (searchText.length < 3) return; // prevent stale results

        const users = response?.payload || [];
        const filtered = condition
          ? users.filter(
              (x) =>
                x.email.toLowerCase() !== auth.user.profile.email.toLowerCase(),
            )
          : users;

        const mapped = filtered.map((user, index) => ({
          label: user.name,
          value: user.email,
          key: `${user.email}-${index}`,
        }));

        setOptions(mapped);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          console.log('Previous fetchUsers request canceled');
        }
        setLoading(false);
      });
  };

  return (
    <Select
      showSearch
      allowClear
      labelInValue
      value={selected ?? undefined}
      onSearch={(val) => setSearchText(val)}
      onChange={(val) => handleSelect(val)}
      placeholder={placeholder}
      notFoundContent={loading ? <Spin size="small" /> : null}
      filterOption={false}
      options={options}
      style={{ width: '100%', ...style }}
      suffixIcon={null}
      className="custom-bordered-input"
      disabled={disabled}
    />
  );
};

export default ResourceSearchableDropdown;
